import classNames from 'classnames';
import Color from 'color';
import { Property as P } from 'csstype';
import _, { defaults, filter, find, isFunction, isNil, map, orderBy, reduce, stubFalse, upperFirst } from 'lodash';
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { startTransition } from 'react';
import { ConditionalStyles as IDataTableConditionalRowStyles } from 'react-data-table-component';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FaFilter } from 'react-icons/fa';
import { Redirect, Switch, useHistory, useLocation, useRouteMatch} from 'react-router';
import { Badge, Nav, Navbar, NavbarBrand, NavbarText, NavItem, NavLink } from 'reactstrap';
import { CenteredContainer } from '.';
import { Column, Context, convertHandlingToRow, HandlingColumn, handlingColumns, HandlingRow, HandlingStatus, Row as DataRow, RowKeys } from '../data';
import { useCurrentUser } from '../hooks';
import useFilters, { FilterDescription } from '../hooks/useFilters';
import styled, { css, useTheme } from '../themes/themed-styled-components';
import { numericTierFromBrt } from '../util';
import { HacpackQueryResponse, Handling, HandlingType, RoleName, Terminal } from './../types';
import { Handling as GraphHandling, Terminal as GraphTerminal } from './../types/graph';
import DataTable from './DataTable';
import HandlingsDeleteButton, { HandlingWithIdAndUnitNumber } from './Handling/HandlingsDeleteButton';
import HandlingDetail from './HandlingDetail';
import Loading from './Loading';
import PrivateRoute from './PrivateRoute';
import QueryLoader, { QueryLoaderContext } from './QueryLoader';
import TerminalHandlingsAssignDateButton from './TerminalHandlingsAssignDateButton';
import { GridLayout } from './ui';




type TerminalHandlingsProps = {
    page: TerminalHandlingsPage,
    terminal: Terminal
}

type StatusColor = {
    status: HandlingStatus, color: Color
};


type ColorFilter<T = any> = {
    name: 'any' | string,
    title: string,
    filter?: (val: T) => boolean,
    count: number
    color: Color | null
}

type ColumnName<T, TRow extends DataRow<T>> = RowKeys<T, TRow>;

type TerminalHandlingsPageData<T, TRow extends DataRow<T>> = {
    filter?: ((t: T) => boolean),
    columns?: (context: Context) => (Partial<Record<RowKeys<T, TRow>, boolean | Partial<Column<TRow>> & { filter?: Omit<FilterDescription<HandlingRow, keyof HandlingRow>, 'field' | 'title'> }>>)
}

type TerminalHandlingsPage = {
    key: string,
    query: string,
    handlings: TerminalHandlingsPageData<Handling, HandlingRow>
}

type HandlingColumnName = ColumnName<Handling, HandlingRow>;

type HandlingColumnsFactory = Partial<Record<HandlingColumnName, boolean | Partial<HandlingColumn> & { filter?: Omit<FilterDescription<HandlingRow, keyof HandlingRow>, 'field' | 'title'> }>>;

export const terminalHandlingsPages: TerminalHandlingsPage[] = [
    {
        key: 'yard',
        query: `
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        port
        authorityType
        areas {
            id
            name
            description
        }
        handlings(checkedIn:true) { 
            id
            creationDate 
            loadingUnitIdentifier
            checkedIn
            checkedOut
            checkInDate 
            shippingAgencyOrCarrier
            loadingUnitSize
            loadingUnitSizeFromGate
            type
            ship
            boardingShip
            unboardingShip
            voyage
            area 
            bay 
            row 
            tier
            equipmentType
            direct
            approvedByTerminal
            checkedByTerminal
            prescriptions
            boardingPrescriptions
            unboardingPrescriptions
            booking
            bookingFromTerminal
            boardingShipFromTerminal
            unboardingShipFromTerminal
            prescriptions
            qsaNotes
            approval
            terminalOpinionDate
            bookingCheckResult
            idSerchim
            chemistPrescriptionsUrl
            assignedDate
            serchimShippingCompany
            storageDays
            shipArrival {
                id
                name
                shippingAgency
                ship
                eta
                etd
            }
            harbourOfficeAuthorization {
                authorization
            }
            portAuthorityAuthorization {
                authorized
                fileNumber
            }
            dangerousGoods {
                id
                authorityId
                unNumber
                packingGroup
                packaging
                weight
                numberOfPackages
                adrClass
                adrSecondaryClasses
                limitedQuantity
            }
        }
    }
}
        `,
        handlings: {
            filter: (h) => h.checkedIn === true && h.checkedOut === false,
            columns: (context) => {
                const cols: HandlingColumnsFactory = {
                    'id': true,
                    'loadingUnitIdentifier': true
                };

                const terminal = context.terminal
                if (isNil(terminal)) return cols;

                const port = terminal.port
                const terminalName = terminal.name

                if (isNil(port) || isNil(terminalName)) return cols;

                if (port === 'LA SPEZIA') {
                    cols.loadingUnitIdentifier = {
                        cell: (row, rowIndex, column, id) => {
                            return row.loadingUnitIdentifier?.trim() === row.idSerchim?.toString().trim()
                                ? "-" : handlingColumns.loadingUnitIdentifier?.cell?.(row, rowIndex, column, id) ?? null
                        }
                    }
                    cols.idSerchim = true
                    cols.booking = true;
                    cols.qsaNotesOrPrescriptions = { wrap: true }
                }

                if (terminalName === 'REBORA') {
                    cols.loadingUnitSizeFromGate = true;
                }

                cols.status = true;

                if (['LIVORNO','TRIESTE'].includes(port)) {
                    cols.shippingAgencyOrCarrier = true;
                }

                cols.area = true;
                cols.bay = true;
                cols.row = true;
                cols.tier = true;

                if (port === 'LA SPEZIA') {
                    cols.tier = {
                        format: ({ tier }) => numericTierFromBrt(tier)
                    };
                    cols.unNumbers = true;
                    cols.adrClasses = true;
                }

                cols.unboardingShip = true;
                cols.boardingShip = true;

                if (port === 'LA SPEZIA') {

                    cols.unboardingShip = false;
                    cols.boardingShip = false;
                    //cols.type = {
                    //    selector: (row) => {
                    //        switch (row.source?.type) {
                    //            case HandlingType.Boarding: return 'Imbarco'
                    //            case HandlingType.Unboarding: return 'Sbarco'
                    //        }
                    //        return ''
                    //    }
                    //}
                    cols.ship = true
                    if (context.terminal?.port === "TRIESTE") {
                        cols.terminalVoyageEta = true;
                    }
                    //cols.boardingShip = {
                    //    format: ({ source }) => source?.type === HandlingType.Boarding ? source?.ship : null,
                    //    filter: {
                    //        type: 'list',
                    //        selector: (data) => data.source?.type === HandlingType.Boarding
                    //            ? data.source?.ship ?? null
                    //            : null
                    //    }
                    //}
                    //cols.unboardingShip = {
                    //    format: ({ source }) => source?.type === HandlingType.Unboarding ? source?.ship : null
                    //}
                    cols.storageDays = true;
                }

                cols.checkInDate = true;

                if (['LIVORNO', 'TRIESTE'].includes(port)) {
                    cols.checkInDate = true;
                    cols.portAuthorityAuthorizationFileNumber = port === 'LIVORNO';
                    cols.equipmentType = true;
                    cols.unNumbers = true;
                    cols.adrClasses = true;
                    cols.limitedQuantity = true;
                    cols.totalWeight = true;
                } else if (terminalName === 'MEDCENTER' || terminalName === 'LSCT') {
                    cols.prescriptions = { wrap: true }
                } else if (port === 'GENOVA') {
                    cols.prescriptions = { wrap: true }
                } else if (port === 'VENEZIA') {
                    cols.checkInDate = true;
                    cols.prescriptions = { wrap: true }
                }

                if (!['LIVORNO', 'LA SPEZIA', 'TARANTO'].includes(port)) {
                    cols.approvedByTerminal = true;
                }

                if (port === 'GENOVA') {
                    cols.unNumbers = true;
                    cols.adrClasses = true;
                    cols.limitedQuantity = true;
                    cols.approvedByHarbourOffice = true;
                    
                }

                if (port === 'TARANTO') {
                    cols.approval = true;
                }
                
                return cols;
            }
        }
    },
    {
        key: 'exports',
        query: `
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        port
        authorityType
        areas {
            id
            name
            description
        }
        handlings(checkedIn:false,types:[${HandlingType.Boarding},${HandlingType.TranshipmentBoarding}]) {
            id
            creationDate 
            loadingUnitIdentifier
            checkedIn
            checkedOut
            checkInDate 
            shippingAgencyOrCarrier
            loadingUnitSize
            loadingUnitSizeFromGate
            type
            ship
            boardingShip
            unboardingShip
            voyage
            area 
            bay 
            row 
            tier
            equipmentType
            direct
            approvedByTerminal
            checkedByTerminal
            prescriptions
            boardingPrescriptions
            unboardingPrescriptions
            booking
            bookingFromTerminal
            boardingShipFromTerminal
            unboardingShipFromTerminal
            qsaNotes
            approval
            terminalOpinionDate
            bookingCheckResult
            idSerchim    
            goodsDataModified
            goodsDataModifiedAt
            prescriptionsModified
            prescriptionsModifiedAt
            chemistPrescriptionsUrl
            assignedDate
            requestedDate
            serchimShippingCompany
            terminalVoyageEta
            harbourOfficeAuthorization {
                authorization
            }
            portAuthorityAuthorization {
                authorized
                fileNumber
            }
            dangerousGoods {
                id
                authorityId
                unNumber
                packingGroup
                packaging
                weight
                numberOfPackages
                adrClass
                adrSecondaryClasses
                limitedQuantity
            }
        }
    }
}
        `,
        handlings: {
            filter: (h) => h.checkedIn === false && (h.type === HandlingType.Boarding || h.type === HandlingType.TranshipmentBoarding),
            columns: (context) => {
                const cols: HandlingColumnsFactory = {
                    'id': true,
                    'loadingUnitIdentifier': true,
                    'status': true,
                };

                const terminal = context.terminal
                if (isNil(terminal)) return cols;

                const port = terminal.port
                const terminalName = terminal.name

                if (isNil(port) || isNil(terminalName)) return cols;
                //cols.status = true;

                if (context.terminal?.port === 'LIVORNO') {
                    cols.approvedByTerminal = true;
                    cols.approvedByAuthority = true;
                    cols.portAuthorityAuthorizationFileNumber = true;
                }


                if (context.terminal?.port === 'LA SPEZIA') {

                    cols.loadingUnitIdentifier = {
                        omit: true,
                        cell: (row, rowIndex, column, id) => {
                            console.debug(row)
                            return row.loadingUnitIdentifier?.trim() === row.idSerchim?.toString().trim()
                                ? "-" : handlingColumns.loadingUnitIdentifier?.cell?.(row, rowIndex, column, id) ?? null
                        }
                    }
                    cols.idSerchim = true
                    cols.booking = true
                    cols.requestedDate = true
                    cols.ship = true;
                    cols.assignedDate = true

                }

                cols.ship = true;
                if (context.terminal?.port === "TRIESTE") {
                    cols.terminalVoyageEta = true;
                }
                cols.voyage = true;
                cols.loadingUnitSize = true;
                cols.unNumbers = true;
                cols.adrClasses = true;
                cols.limitedQuantity = true;
                cols.shippingAgencyOrCarrier = true;
                cols.totalWeight = true;
                cols.dangerousGoodsPackaging = true;

                if (port === "TREISTE") {
                    cols.equipmentType = true;
                }

                if (context.terminal?.port !== 'LIVORNO' && context.terminal?.port !== 'TARANTO') {
                    cols.approvedByTerminal = true;
                }

                if (context.terminal?.port === 'GENOVA') {
                    cols.approvedByHarbourOffice = true;
                }

                if (context.terminal?.port === 'TARANTO') {
                    cols.approval = true;
                }

                return cols;
            }
        }
    },
    {
        key: 'imports',
        query: `
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        port
        authorityType
        areas {
            id
            name
            description
        }
        handlings(checkedIn:false,types:[${HandlingType.Unboarding},${HandlingType.TranshipmentUnboarding}]) {
            id
            creationDate 
            loadingUnitIdentifier
            checkedIn
            checkedOut
            checkInDate 
            shippingAgencyOrCarrier
            loadingUnitSize
            loadingUnitSizeFromGate
            type
            ship
            boardingShip
            unboardingShip
            voyage
            area 
            bay 
            row 
            tier
            equipmentType
            direct
            approvedByTerminal
            checkedByTerminal
            prescriptions
            boardingPrescriptions
            unboardingPrescriptions
            booking
            bookingFromTerminal
            boardingShipFromTerminal
            unboardingShipFromTerminal
            qsaNotes
            approval
            terminalOpinionDate
            bookingCheckResult
            idSerchim
            chemistPrescriptionsUrl
            assignedDate
            serchimShippingCompany
            terminalVoyageEta
            harbourOfficeAuthorization {
                authorization
            }
            portAuthorityAuthorization {
                authorized
                fileNumber
            }
            dangerousGoods {
                id
                authorityId
                unNumber
                packingGroup
                packaging
                weight
                numberOfPackages
                adrClass
                adrSecondaryClasses
                limitedQuantity
            }
        }
    }
}
        `,
        handlings: {

            filter: (h) => h.checkedIn === false && (h.type === HandlingType.Unboarding || h.type === HandlingType.TranshipmentUnboarding),
            columns: (context) => {
                const cols: HandlingColumnsFactory = {
                    'id': true,
                    'loadingUnitIdentifier': true,
                    'status': true,
                };

                const terminal = context.terminal
                if (isNil(terminal)) return cols;

                const port = terminal.port
                const terminalName = terminal.name

                if (isNil(port) || isNil(terminalName)) return cols;

                //cols.status = true;

                if (context.terminal?.port === 'LIVORNO') {
                    cols.approvedByTerminal = true;
                    cols.approvedByAuthority = true;
                    cols.portAuthorityAuthorizationFileNumber = true;
                }

                if (context.terminal?.port === 'LA SPEZIA') {
                    cols.loadingUnitIdentifier = {
                        cell: (row, rowIndex, column, id) => {
                            return row.loadingUnitIdentifier?.trim() === row.idSerchim?.toString().trim()
                                ? "-" : handlingColumns.loadingUnitIdentifier?.cell?.(row, rowIndex, column, id) ?? null
                        }
                    }
                    cols.idSerchim = true
                    cols.booking = true;
                }

                cols.ship = true;

                if (context.terminal?.port === "TRIESTE") {
                    cols.terminalVoyageEta = true;
                }

                cols.voyage = true;
                cols.loadingUnitSize = true;
                cols.unNumbers = true;
                cols.adrClasses = true;
                cols.limitedQuantity = true;
                cols.shippingAgencyOrCarrier = true;
                cols.totalWeight = true;
                cols.dangerousGoodsPackaging = true;

                if (port === "TREISTE") {
                    cols.equipmentType = true;
                }

                if (context.terminal?.port !== 'LIVORNO' && context.terminal?.port !== 'TARANTO') {
                    cols.approvedByTerminal = true;
                }

                if (context.terminal?.port === 'GENOVA') {
                    cols.approvedByHarbourOffice = true;
                }

                if (context.terminal?.port === 'TARANTO') {
                    cols.approval = true;
                }



                return cols;
            }
        }
    },
    {
        key: 'transits', query: `
query terminal($id: Int!) {
    terminal (id: $id) {
        id
        name
        port
        authorityType
        areas {
            id
            name
            description
        }
        handlings(checkedIn:false,types:[${HandlingType.Transit}]) {
            id
            creationDate 
            loadingUnitIdentifier
            checkedIn
            checkedOut
            checkInDate 
            shippingAgencyOrCarrier
            loadingUnitSize
            loadingUnitSizeFromGate
            type
            ship
            boardingShip
            unboardingShip
            voyage
            area 
            bay 
            row 
            tier
            equipmentType
            direct
            approvedByTerminal
            checkedByTerminal
            prescriptions
            boardingPrescriptions
            unboardingPrescriptions
            booking
            bookingFromTerminal
            boardingShipFromTerminal
            unboardingShipFromTerminal
            qsaNotes
            harbourOfficeAuthorization {
                authorization
            }
            portAuthorityAuthorization {
                authorized
                fileNumber
            }
            dangerousGoods {
                id
                unNumber
                packingGroup
                packaging
                weight
                numberOfPackages
                adrClass
                adrSecondaryClasses
                authorityId
                limitedQuantity
            }
        }
    }
}
        `,
        handlings: {
            filter: (h) => h.checkedIn === false && (h.type === HandlingType.Transit),
            columns: (context) => {
                const cols: HandlingColumnsFactory = {
                    'id': true,
                    'loadingUnitIdentifier': true,
                    'status': true,
                };

                const terminal = context.terminal
                if (isNil(terminal)) return cols;

                const port = terminal.port
                const terminalName = terminal.name

                if (isNil(port) || isNil(terminalName)) return cols;

                //cols.status = true;

                if (context.terminal?.port === 'LIVORNO') {
                    cols.approvedByTerminal = true;
                    cols.approvedByAuthority = true;
                    cols.portAuthorityAuthorizationFileNumber = true;
                }

                cols.ship = true;
                if (context.terminal?.port === "TRIESTE") {
                    cols.terminalVoyageEta = true;
                }
                cols.voyage = true; 
                cols.unNumbers = true;
                cols.adrClasses = true;
                cols.limitedQuantity = true;
                cols.shippingAgencyOrCarrier = true;
                cols.totalWeight = true;
                cols.dangerousGoodsPackaging = true;


                if (context.terminal?.port !== 'LIVORNO' && context.terminal?.port !== 'TARANTO') {
                    cols.approvedByTerminal = true;
                }

                if (context.terminal?.port === 'GENOVA') {
                    cols.approvedByHarbourOffice = true;
                }

                return cols;
            }
        }
    }
]

const TerminalHandlingsLayout = styled.div<{ $left: boolean }>`
    width: 100%;
    height: 100%;
    flex-direction: column;
    overflow: hidden;

    /* grid container settings */
    display: grid;
    grid-template-columns: ${({ $left }) => $left ? 'auto' : null} 1fr;
    grid-template-rows: 1fr;
    grid-template-areas:
        "${({ $left }) => $left ? 'left' : null} content";
`;

const TerminalHandlingsLayoutElement = styled.div<{ $area: P.GridArea }>`
    grid-area: ${(props) => props.$area};
    min-height: 0;
    ${({ $area }) => $area === 'content' ? 'overflow: auto;' : null}
`;

const TerminalHandlingsTable = ({ terminal, cols, tableRows, onSort, sortField, sortAsc, onRowClicked, conditionalRowStyles, baseUrl, onDetailsClose, pageKey, onSelectedRowsChange, selectableRows, clearSelectedRows }: any) => {

    const { params } = useRouteMatch();

    const loadingUnitIdentifierOrId = (params as any)['loadingUnitIdentifier'] ?? null;

    const { reload, loading } = useContext(QueryLoaderContext);

    const activeRow = useMemo(() => {
        const id = Number(loadingUnitIdentifierOrId)

        if (!isNaN(id)) {
            const activeRow = reduce(tableRows, (activeRow, row) => {
                if (loadingUnitIdentifierOrId !== null && row.id === id) {
                    row.isActive = true;
                    return row;
                }
                row.isActive = false;
                return activeRow;
            }, null as null | HandlingRow)

            if (activeRow !== null) {
                return activeRow
            }
        }
        return reduce(tableRows, (activeRow, row) => {
            if (loadingUnitIdentifierOrId !== null && row.source?.loadingUnitIdentifier?.toLowerCase() === loadingUnitIdentifierOrId) {
                row.isActive = true;
                return row;
            }
            row.isActive = false;
            return activeRow;
        }, null as null | HandlingRow);

    }, [loadingUnitIdentifierOrId, tableRows]);

    //const terminalName = terminal.name;

    const tableCols = useMemo(() => {
        if (activeRow === null) {
            return cols
        }

        const minIndex = process.env.NODE_ENV === 'development' ? 1 : 0

        const colId = (cols as HandlingColumn[]).filter((col) => col.omit !== true).find((_col, i, arr) => (i >= minIndex || arr.length === 1))?.id!

        //const column = (pageKey === 'exports' && terminalName === 'TERMINAL DEL GOLFO') ? 'idSerchim' : 'loadingUnitIdentifier'

        return map(cols, (col) => defaults(col.id === colId ? {} : { omit: true }, col))
    }, [cols, activeRow]);

    if (loadingUnitIdentifierOrId !== null) {
        if (activeRow === null) {
            return <Redirect to={baseUrl} />;
        }

        if (activeRow.id !== Number(loadingUnitIdentifierOrId)) {
            return <Redirect to={`${baseUrl}/${activeRow.id}`} />;
        }
    }

    const table = <TerminalHandlingsLayoutElement $area={activeRow !== null ? 'left' : 'content'}>
        <DataTable<HandlingRow>
            noHeader
            columns={tableCols}
            data={tableRows}
            striped
            pointerOnHover
            highlightOnHover
            onSort={onSort}
            defaultSortFieldId={sortField}
            defaultSortAsc={sortAsc}
            onRowClicked={onRowClicked}
            conditionalRowStyles={conditionalRowStyles}
            selectableRowDisabled={() => loading}
            selectableRows={selectableRows}
            onSelectedRowsChange={onSelectedRowsChange}
            clearSelectedRows={clearSelectedRows}
        />
        {/* onSelectedRowsChange={(ev) => { console.debug(ev) }}*/}
    </TerminalHandlingsLayoutElement>;

    const selectedHandlingId = activeRow?.id ?? null;

    const details = selectedHandlingId !== null ? <TerminalHandlingsLayoutElement $area="content">
        <QueryLoader<HacpackQueryResponse, { id: number }> reloadInterval={600000} query={`
query handling($id: Long!) {
    handling (id: $id) {
        id
        terminalName
        creationDate 
        loadingUnitIdentifier
        checkedIn
        checkedOut
        checkInDate 
        shippingAgencyOrCarrier
        loadingUnitSize
        loadingUnitSizeFromGate
        type
        ship
        boardingShip
        unboardingShip
        voyage
        area 
        bay 
        row 
        tier
        equipmentType
        direct
        approvedByTerminal
        checkedByTerminal
        prescriptions
        boardingPrescriptions
        unboardingPrescriptions
        booking
        bookingFromTerminal
        boardingShipFromTerminal
        unboardingShipFromTerminal
        qsaNotes
        idSerchim
        terminalOpinionDate
        bookingCheckResult
        chemistPrescriptionsUrl
        harbourOfficeAuthorization {
            authorization
        }
        portAuthorityAuthorization {
            authorized
            fileNumber
            downloadUrl
        }
        dangerousGoods {
            id
            unNumber
            packingGroup
            packaging
            weight
            numberOfPackages
            provision274
            flashPoint
            adrClass
            adrSecondaryClasses
            marinePollutant
            limitedQuantity
            fumigated
            coolingAgents
            provision274
            elevatedTemperature
            emergencyCardUrl
            properShippingName
            authorityId
            safetyDataSheet {
                id
                downloadUrl
            }
        }
        chemistDocuments {
            id
            documentTitle
            downloadUrl
        }
    }
}  `} variables={{ id: selectedHandlingId as number }} key={selectedHandlingId} render={data => <HandlingDetail terminal={terminal} handling={data.handling as GraphHandling} onDelete={reload} onClose={onDetailsClose} />} />
    </TerminalHandlingsLayoutElement> : null;

    return <TerminalHandlingsLayout $left={details !== null}>
        {table}
        {details}
    </TerminalHandlingsLayout>;
};

const TerminalHandlings = ({ page, terminal }: TerminalHandlingsProps) => {

    const { url } = useRouteMatch();
    //const { path } = useRouteMatch();

    //console.log(generatePath(path, {
    //    loadingUnitIdentifier: null,
    //    detail: null
    //}));

    //console.log({ url, params, path, baseUrl });

    const { reload } = useContext(QueryLoaderContext);

    const [filtersVisible, setFiltersVisible] = useState<boolean>(false);

    const toggleFiltersVisible = useCallback(() => {
        setFiltersVisible((filtersVisible) => !filtersVisible)
    }, [setFiltersVisible]);

    const handlings = useMemo(() => terminal?.handlings ?? [], [terminal]);
    const theme = useTheme();

    const history = useHistory();
    const { t } = useTranslation('common');

    const pageKey = useMemo(() => page.key, [page]);
    //const page = useMemo<Page|null>(() => pageKey!==null?pages[pageKey]:null,[pageKey,pages])

    const location = useLocation();
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const [activeFilterName, setActiveFilterName] = useState<string | null>(() =>
        location.hash ? location.hash.substring(1) : null);

    useEffect(() => {
        if (location.hash.length > 0) {
            history.replace(location.pathname, { replace: true });
        }
    }, [history, location.hash, location.pathname]);

    const [selectedHandlings, setSelectedHandlings] = useState<Handling[]>([]);

    const ctx = useMemo<Context>(() => ({ terminal: terminal as Terminal, url, history }), [terminal, url, history]);

    const handlingColumnsFactory = useMemo<HandlingColumnsFactory>(() => (page?.handlings?.columns?.(ctx) ?? {}), [page, ctx]);

    const handlingFilter = useMemo(() => page?.handlings?.filter ?? stubFalse, [page]);

    const [cols, availableFilters] = useMemo(() => reduce(handlingColumnsFactory, ([cols, filters], factory, key) => {
        if (factory === false || factory === undefined) {
            return [cols, filters];
        }

        const k = key as string;
        const pcol = factory === true ? {} : factory;
        const name = t([
            `terminal.data.handlings.columns.${k}.name`,
            `terminal.${pageKey}.data.columns.${k}.name`,
            `terminal.data.columns.${k}.name`,
            `data.columns.${k}.name`
        ]);

        const column = (handlingColumns as any)[k];

        //cols.push(defaults({ id: k }, pcol, { selector: k, name }, column) as HandlingColumn);
        const rcol = defaults(pcol, column, { selector: (col: any) => col[k], name, id: k }) as HandlingColumn;
        //const rcol = defaults({ id: k }, { selector: (col: any) => col[k], pcol,  name }, column) as HandlingColumn;
        cols.push(rcol);

        const filter = column?.filter;

        if (!isNil(filter)) {
            const f = defaults(filter, {
                title: name,
                field: k as keyof HandlingRow,
                selector: isFunction(rcol.selector) ? rcol.selector : undefined
            })

            filters.push(f)
            //filters.push({ title: name, field: k as keyof HandlingRow, type: column.filter.type, selector: isFunction(rcol.selector)?  });
        }

        return [cols, filters];

    }, [[], []] as [HandlingColumn[], FilterDescription<HandlingRow>[]]), [t, pageKey, handlingColumnsFactory]);

    const rows = useMemo(() => _(handlings).filter(handlingFilter).map((handling) => convertHandlingToRow(handling, ctx, map(cols, (col) => col.id as HandlingColumnName))).value(), [handlings, cols, ctx, handlingFilter]);

    const { filtered, form: filtersForm, count: filtersCount } = useFilters({ data: rows, availableFilters });

    const statusColors = useMemo<StatusColor[]>(() => [
        { status: HandlingStatus.Approved, color: Color(theme.green) },
        { status: HandlingStatus.ApprovedWithPrescriptions, color: Color(theme.yellow) },
        { status: HandlingStatus.NotYetAuthorized, color: Color(theme.red) },
        { status: HandlingStatus.NotYetViewed, color: Color(theme.orange) },
        { status: HandlingStatus.ApprovedByTerminal, color: Color(theme.orange) },
        { status: HandlingStatus.MissingData, color: Color(theme.purple) },
        { status: HandlingStatus.Direct, color: Color(theme.blue) }
    ], [theme]);

    //const statusColorsRecord: Record<HandlingStatus, Color> = ({ theme }) => [
    //    { status: HandlingStatus.Approved, color: Color(theme.green) },
    //    { status: HandlingStatus.ApprovedWithPrescriptions, color: Color(theme.yellow) },
    //    { status: HandlingStatus.NotYetAuthorized, color: Color(theme.red) },
    //    { status: HandlingStatus.NotYetViewed, color: Color(theme.orange) },
    //    { status: HandlingStatus.MissingData, color: Color(theme.purple) },
    //    { status: HandlingStatus.Direct, color: Color(theme.blue) }
    //];

    const conditionalRowStyles = useMemo<IDataTableConditionalRowStyles<HandlingRow>[]>(() => {
        return reduce<StatusColor, IDataTableConditionalRowStyles<HandlingRow>[]>(statusColors, (styles, { status, color }, index, statusColors) => {
            const unevenBg = color.lightness(80);
            const evenBg = color.lightness(75);
            const unevenBgHover = color.lightness(65);
            const evenBgHover = color.lightness(60);
            const borderColor = color.lightness(50);

            styles.push({
                when: row => (row.status ?? HandlingStatus.None) === status && row.isActive === false,
                style: {
                    color: unevenBg.isLight() ? theme.bodyColor : theme.white,
                    backgroundColor: unevenBg.toString(),
                    borderBottomColor: unevenBg.darken(0.5).toString(),
                    '&:nthChild(even)': {
                        color: evenBg.isLight() ? theme.bodyColor : theme.white,
                        backgroundColor: evenBg.toString(),
                    },
                    '&:hover': {
                        color: unevenBgHover.isLight() ? theme.bodyColor : theme.white,
                        backgroundColor: unevenBgHover.toString(),
                        outlineColor: borderColor.toString(),
                        borderBottomColor: borderColor.toString(),
                    },
                    '&:hover:nthChild(even)': {
                        color: evenBgHover.isLight() ? theme.bodyColor : theme.white,
                        backgroundColor: evenBgHover.toString(),
                    }
                }
            } as IDataTableConditionalRowStyles<HandlingRow>);

            return styles;
        }, [] as IDataTableConditionalRowStyles<HandlingRow>[]);
    }, [theme, statusColors]);

    const filters = useMemo(() => {
        const baseFilters: ColorFilter<HandlingRow>[] = [/*
            {
                name: 'any',
                title: t([
                    'terminal.data.handlings.filters.any',
                    `terminal.data.${pageKey}.filters.any`,
                    'terminal.data.filters.any',
                    'data.filters.any'
                ]),
                filter: stubTrue,
                count: rows.length,
                color: null
            }
        */];

        return orderBy(reduce<StatusColor, ColorFilter<HandlingRow>[]>(statusColors, (filters, { status, color }, index, statusColors) => {
            const f = (row: HandlingRow): boolean => row.status === status;
            const count = filter(filtered, f).length;

            if (count === 0) {
                return filters;
            }

            const name = `status${upperFirst(status)}`

            filters.push({
                name: `status${upperFirst(status)}`,
                title: t([
                    `terminal.data.handlings.filters.${name}`,
                    `terminal.data.${pageKey}.filters.${name}`,
                    `data.filters.${name}`,
                    `terminal.data.filters.${name}`,
                ]),
                count,
                filter: f,
                color: color.lightness(80)
            });

            return filters
        }, baseFilters), 'count', 'desc');
    }, [t, filtered, pageKey, statusColors]);


    //const filterTabs = useMemo(() => map(filters, ({ name, count, title, color }) => {
    //    const onClick = (ev) => {
    //        ev.preventDefault();
    //        setActiveFilterName(name);
    //    }

    //    const active = name === activeFilterName;

    //    const bgColor = active ? color : color.lightness(85);
    //    const badgeBgColor = color.lightness(40);

    //    return <NavItem key={name}>
    //        <NavLink href="#" className={active ? 'active' : undefined} onClick={onClick} style={{
    //            backgroundColor: bgColor?.toString(),
    //            color: bgColor.isLight() ? theme.black : theme.white
    //        }}>
    //            {title} <Badge style={{
    //                backgroundColor: badgeBgColor.toString(),
    //                color: badgeBgColor.isLight() ? theme.black : theme.white
    //            }}>{count}</Badge>
    //        </NavLink>
    //    </NavItem>
    //}), [filters, activeFilterName]);

    const filterTabs = useMemo(() => map(filters, (filter, i) => <ColorFilterTab key={i} filter={filter} active={filter.name === activeFilterName} onActivated={setActiveFilterName} />), [filters, activeFilterName]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);

    const toggleClearSelectedRows = () => setClearSelectedRows(clearSelectedRows => !clearSelectedRows)

    useEffect(() => {
        setActiveFilterName((prevActiveFilterName) => {
            if ((find(filters, { name: prevActiveFilterName })) === null) {
                return null;
            }
            return (find(filters, { name: prevActiveFilterName }) as ColorFilter<HandlingRow> | null | undefined)?.name ?? null;
        })
    }, [filters, setActiveFilterName])

    const activeFilter = useMemo<ColorFilter<HandlingRow> | null>(() => activeFilterName === null ? null : find(filters, { name: activeFilterName }) ?? null, [activeFilterName, filters]);

    const tableRows = useMemo(() => _(activeFilter === null ? filtered : filter(filtered, activeFilter.filter)).map((row) => defaults({ isActive: false }, row)).value(), [filtered, activeFilter]);

    const onSort = useMemo(() => (column: Column<HandlingRow>, sortDirection: 'asc' | 'desc') => {
        setSortField(column.sortField);
        setSortAsc(sortDirection === 'asc');
    }, [setSortField, setSortAsc]);

    const onDetailsClose = useMemo(() => () => {
        history.push(`${url}`);
    }, [history, url]);

    const onRowClicked = useMemo(() => (row: HandlingRow) => {
        history.push(`${url}/${row.id}`);
    }, [history, url]);

    const onTitleClicked = useMemo<React.MouseEventHandler<HTMLAnchorElement>>(() => (ev) => {
        ev.preventDefault();
        setActiveFilterName(null);
    }, [setActiveFilterName]);

    const showTabs = useMemo(() => (filters.length !== 1 || filters[0].count !== filtered?.length), [filters, filtered]);

    const onSelectedRowsChange = useCallback(({ selectedCount, selectedRows }: {
        allSelected: boolean;
        selectedCount: number;
        selectedRows: HandlingRow[];
    }) => {
        if (selectedCount === 0) {
            setSelectedHandlings([])
            toggleClearSelectedRows()
            return;
        }

        const selectedIds: number[] = selectedRows.map(r => r?.source?.id).filter((id): id is number => id !== undefined)

        const selectedHandlings = handlings.filter(h => {
            const id = h.id
            return id !== undefined && selectedIds.includes(id)
        })

        startTransition(() => {
            setSelectedHandlings(selectedHandlings)
            if (selectedHandlings.length === 0) toggleClearSelectedRows()
        })
    }, [handlings]);

    const { isSuperuser, hasRoleForTerminal } = useCurrentUser();

    const terminalId = terminal.id!

    const canDeleteHandlings = (isSuperuser || hasRoleForTerminal(RoleName.ADMIN, terminalId));

    const canAssignDates = page.key === 'exports' && (isSuperuser || hasRoleForTerminal(RoleName.QSA, terminalId));

    const selectableRows = canDeleteHandlings || canAssignDates;

    const handleAfterDelete = useCallback(() => {
        startTransition(() => {
            setSelectedHandlings([]);
            toggleClearSelectedRows();
            reload();
        })
    }, [setSelectedHandlings, reload])

    const header = useMemo(() => (
        <header>
            <Navbar className="navbar ng-white border-bottom box-shadow" style={{ paddingBottom: 0 }} light>
                <div style={{ display: 'flex' }}>
                    <NavbarBrand href="#" onClick={onTitleClicked}>
                        {t(`terminal.${pageKey}.title`)}
                        &nbsp;
                        {showTabs ? <Badge>{filtered.length}</Badge> : <StyledBadge color={filters[0].color}>{filtered.length}</StyledBadge>}
                    </NavbarBrand>
                    {filterTabs === null || !showTabs ? <NavbarText><FilterNameInTitle filter={filters[0]} /></NavbarText> : <div className="flex-row flex-nowrap">
                        <Nav tabs className="flex-row flex-nowrap">{filterTabs}</Nav>
                    </div>}
                </div>

                <div className="navbar-right pb-1">
                    <div style={{ display: 'flex', gap: '.25rem' }}>
                        {canAssignDates && <TerminalHandlingsAssignDateButton handlings={selectedHandlings as GraphHandling[]} terminal={terminal as GraphTerminal} />}
                        {canDeleteHandlings && <HandlingsDeleteButton multi handlings={selectedHandlings as HandlingWithIdAndUnitNumber[]} onAfterDelete={handleAfterDelete} />}
                        <button className={classNames({ btn: true, 'btn-secondary': true, active: filtersVisible })} onClick={() => toggleFiltersVisible()}>
                            <FaFilter /> {t('Filtra')} {(filtersCount > 0) ? <Badge>{filtersCount}</Badge> : null}
                        </button>
                    </div>
                </div>
            </Navbar>
        </header>), [onTitleClicked, t, pageKey, showTabs, filtered.length, filters, filterTabs, canAssignDates, selectedHandlings, terminal, canDeleteHandlings, handleAfterDelete, filtersVisible, filtersCount, toggleFiltersVisible]);

    if (terminal?.handlings === undefined) {
        return <Loading />;
    }

    const content = <TerminalHandlingsTable
        pageKey={pageKey}
        terminal={terminal}
        cols={cols}
        tableRows={tableRows}
        onSort={onSort}
        sortField={sortField}
        sortAsc={sortAsc}
        onRowClicked={onRowClicked}
        conditionalRowStyles={conditionalRowStyles}
        baseUrl={url}
        onDetailsClose={onDetailsClose}
        onSelectedRowsChange={onSelectedRowsChange}
        selectableRows={selectableRows}
        clearSelectedRows={clearSelectedRows}
    />;


    if (rows.length === 0) {
        return <CenteredContainer>{t([
            `terminal.${pageKey}.data.handlings.noUnits`,
            `terminal.data.handlings.noUnits`,
            `terminal.${pageKey}.data.noUnits`,
            `terminal.${pageKey}.noUnits`,
            `data.noUnits`
        ])}</CenteredContainer>;
    }

    return (<Fragment>
        <Helmet>
            <title>{t(`terminal.${pageKey}.title`)} :: {terminal?.name} :: {t('app.title')}</title>
        </Helmet>

        <GridLayout header={header} right={filtersVisible ? filtersForm : null}>
            <Switch>
                <PrivateRoute exact path={`${url}`}>{content}</PrivateRoute>
                <PrivateRoute path={`${url}/:loadingUnitIdentifier`}>{content}</PrivateRoute>
            </Switch>
        </GridLayout>
    </Fragment>)
}

type FilterNavLinkProps = {
    $active?: boolean,
    $color?: P.Color,
    $bg?: P.BackgroundColor,
    $hoverColor?: P.Color,
    $hoverBorderColor?: P.BorderColor
};

const filterNavLinkCss = css<FilterNavLinkProps>`
    ${({ theme: { navTabsBorderColor }, $bg }) => $bg !== null ? `background-color: ${$bg} !important; border-color: ${$bg} ${$bg} ${navTabsBorderColor} !important;` : null}
    ${({ $color }) => $color !== null ? `color: ${$color};` : null}
    ${({ $hoverColor, $hoverBorderColor }) => $hoverBorderColor ?? $hoverColor !== null ? `&:hover { 
        ${$hoverColor ? `color: ${$hoverColor};` : null}
        ${$hoverBorderColor ? ` border-color: ${$hoverBorderColor} !important;` : null}
    }` : null}
`;

const activeNavLinkCss = css<FilterNavLinkProps>`
    position: relative;
    &:after {
        content: " ";
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        height: ${({ theme: { borderWidth } }) => borderWidth};
        background-color: ${({ theme: { bodyBg } }) => bodyBg}
    }
`;

const FilterNavText = styled.span`
    white-space: nowrap;
`;

const FilterNavLink = styled(NavLink).attrs<FilterNavLinkProps>(({ active = false, $color = null, $bg = null, $hoverColor = null, $borderColor = null }) => ({ active, $color, $bg, $hoverColor, $borderColor }))`
    border-bottom-width: 0 !important;
    display: flex-row;
    ${({ active }) => active ? activeNavLinkCss : filterNavLinkCss}
`
type FilterTabProps = { filter: ColorFilter, active?: boolean, onActivated?: ((s: string) => void) };
type FilterNameInTitleProps = { filter: ColorFilter };

const StyledBadge = styled(Badge).attrs<FilterNavLinkProps>(({ color, theme: { bodyColor, white } }) => {
    const bg = color?.lightness(40) ?? null

    return {
        $bg: bg,
        $color: bg !== null ? bg.isLight() ? bodyColor : white ?? null : null
    }
})`
    ${({ $bg }) => $bg !== null ? `background-color: ${$bg};` : null}
    ${({ $color }) => $color !== null ? `color: ${$color};` : null}
`

const ColorFilterTab = ({ filter: { title, count, color, name }, active = false, onActivated }: FilterTabProps) => {
    //const theme = useTheme();
    const tabBgColor = color?.lightness(95) ?? null;
    const tabColor = color?.lightness(40) ?? null;
    const tabHoverColor = color?.lightness(35) ?? null;
    const tabHoverBorderColor = color?.lightness(90) ?? null;
    //const badgeBg = color?.lightness(40) ?? null;

    const onClick = useMemo(() => (ev: Event) => {
        ev.preventDefault();
        onActivated?.(name);
    }, [name, onActivated]);

    return <NavItem key={name}>
        <FilterNavLink active={active} href="#" onClick={onClick} $bg={tabBgColor} $color={tabColor} $hoverColor={tabHoverColor} $hoverBorderColor={tabHoverBorderColor} $>
            <FilterNavText>{title} </FilterNavText><span><StyledBadge color={color}>{count}</StyledBadge></span>
        </FilterNavLink>
    </NavItem>;
};

type FilterNameInTitleSpanProps = {
    $color?: P.Color
};

const FilterNameInTitleSpan = styled.span<FilterNameInTitleSpanProps>`
    ${({ $color }) => $color !== null ? `color: ${$color};` : null}
`

const FilterNameInTitle = ({ filter: { title, color } }: FilterNameInTitleProps) => {
    const fgColor = color?.lightness(40).toString();

    return <FilterNameInTitleSpan $color={fgColor}>{title}</FilterNameInTitleSpan>;
};

export default TerminalHandlings;